import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import '../index.css';
import '../RegisterCodeStyles.css';
import registerImg from '../../src/RapidMind 2025.png';
import SocialRegisterButtons from './SocialRegisterButtons';
import { registerUserWithFirebase, getRegisteredUserData } from '../api/register';
import { createUserWithEmailAndPassword, getIdToken, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../Firebase';
import axios from 'axios';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register: authRegister, setUser, user } = useAuth();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    gender: '', // added gender field
  });

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [userData, setUserData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPrivateCode, setShowPrivateCode] = useState(true);
  const [hoveredButton, setHoveredButton] = useState(null);

  const isFreePlan = location.state?.plan === 'free';

  useEffect(() => {
    if (registrationSuccess && isFreePlan) {
      // Redirect free plan users to free trial page
      navigate('/free-trial');
    }
  }, [registrationSuccess, isFreePlan, navigate]);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };
  
  // New handler for gender select change
  const handleGenderChange = e => {
    setFormData({ ...formData, gender: e.target.value });
    if (errors.gender) {
      setErrors({ ...errors, gender: '' });
    }
  };

  const handleCheckboxChange = e => {
    setAgreedToTerms(e.target.checked);
    if (errors.agreedToTerms) {
      setErrors({ ...errors, agreedToTerms: '' });
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const handleContinue = () => {
    navigate('/profile');
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required field validation
    if (!formData.firstName?.trim()) newErrors.firstName = 'Prénom requis';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Nom requis';
    if (!formData.email?.trim()) newErrors.email = 'Email requis';
    if (!formData.gender) newErrors.gender = 'Veuillez sélectionner votre genre'; // gender validation
    
    // Email format validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    // Password validation
    if (!formData.password) newErrors.password = 'Mot de passe requis';
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Mot de passe trop court (min 6 caractères)';
    }
    
    // Simplified password validation for testing
    // Password strength validation (optional for now)
    // if (formData.password && !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
    //   newErrors.password = 'Le mot de passe doit contenir des majuscules, minuscules et chiffres';
    // }

    // Common weak passwords check (optional for now)
    // const weakPasswords = ['123456', 'password', 'qwerty', 'letmein', 'welcome'];
    // if (weakPasswords.includes(formData.password)) {
    //   newErrors.password = 'Mot de passe trop faible';
    // }
    
    // Password confirmation
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirmation du mot de passe requise';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    // Terms agreement
    if (!agreedToTerms) newErrors.agreedToTerms = 'Vous devez accepter les termes et conditions';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Additional security check: prevent rapid form submissions
    if (loading) return;

    if (!validateForm()) return;

    // Generate unique private code and user ID immediately
    const timestamp = Date.now().toString();
    const randomPart = Math.random().toString(36).substring(2, 8).toUpperCase();
    const uniquePrivateCode = `${timestamp.slice(-6)}${randomPart}`;
    const uniqueUserId = `RM${timestamp.slice(-8)}${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

    // Immediately show welcome message on submit click
    const mockUserData = {
      id: uniqueUserId,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.toLowerCase().trim(),
      gender: formData.gender,
      privateCode: uniquePrivateCode
    };

    setRegistrationSuccess(true);
    setUserData(mockUserData);

    setLoading(true);
    setErrors({}); // Clear previous errors

    let firebaseUser = null;

    try {
      // Add small delay to prevent rapid submissions and show loading state
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('🔐 [REGISTER] Starting Firebase user creation...');

      // Step 1: Create user in Firebase Authentication first
      firebaseUser = await createUserWithEmailAndPassword(
        auth,
        formData.email.toLowerCase().trim(),
        formData.password
      );

      console.log('🔐 [REGISTER] Firebase user created successfully:', firebaseUser.user.uid);

      // Step 2: Get Firebase ID token
      const idToken = await getIdToken(firebaseUser.user);
      console.log('🔐 [REGISTER] Firebase ID token obtained');

      // Step 3: Use AuthContext register function with Firebase ID token
      const userData = await authRegister({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.toLowerCase().trim(),
        idToken: idToken, // Include Firebase ID token
        gender: formData.gender
      });

      console.log('🔐 [REGISTER] Registration successful via AuthContext:', userData);
      console.log('🔐 [REGISTER] Private code from backend:', userData?.privateCode);

      // Step 4: Get user data from Firestore database to verify it was saved
      console.log('🔐 [REGISTER] Retrieving user data from Firestore database...');
      try {
        const dbResponse = await getRegisteredUserData(userData.id);
        if (dbResponse.success) {
          console.log('🔐 [REGISTER] User data successfully retrieved from Firestore:', dbResponse.userData);
          console.log('🔐 [REGISTER] Private code from database:', dbResponse.userData.privateCode);
          console.log('✅ [REGISTER] Registration process complete - Data saved to Firebase Firestore database');

          // Update with real user data from backend
          setUserData(dbResponse.userData);

          // Free plan user data is already stored in Firestore by the backend
          if (isFreePlan) {
            console.log('🔐 [REGISTER] Free plan user data stored in Firestore database by backend');
          }
        } else {
          console.warn('⚠️ [REGISTER] Could not retrieve user data from database:', dbResponse.message);
          // Keep the mock data already displayed
        }
      } catch (dbError) {
        console.error('🔐 [REGISTER] Error retrieving user data from database:', dbError);
        // Keep the mock data already displayed
      }
    } catch (error) {
      console.error('🔐 [REGISTER] Registration error:', error);

      // If Firebase user was created but backend failed, sign out from Firebase
      if (firebaseUser) {
        try {
          await signOut(auth);
          console.log('🔐 [REGISTER] Signed out from Firebase due to backend error');
        } catch (signOutError) {
          console.error('🔐 [REGISTER] Error signing out from Firebase:', signOutError);
        }
      }

      // Handle Firebase-specific errors
      if (error.code === 'auth/email-already-in-use') {
        setErrors({ email: 'Cet email est déjà utilisé. Essayez de vous connecter ou utilisez un email différent.' });
      } else if (error.code === 'auth/weak-password') {
        setErrors({ password: 'Mot de passe trop faible' });
      } else if (error.code === 'auth/invalid-email') {
        setErrors({ email: 'Email invalide' });
      } else if (error.code === 'auth/network-request-failed') {
        setErrors({ submit: 'Erreur réseau. Vérifiez votre connexion.' });
      } else if (error.message && error.message.includes('Server error')) {
        setErrors({ submit: 'Erreur serveur. Veuillez réessayer.' });
      } else {
        setErrors({ submit: error.message || 'Erreur lors de l\'inscription' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (registrationSuccess && userData) {
    return (
      <>
        <SEO
          title="Welcome to RapidMind AI - Registration Successful"
          description="Congratulations on joining RapidMind AI! Access your account and start creating with advanced AI tools. Your registration is complete."
          keywords="rapidmind ai registration success, welcome to ai platform, user account created, ai tools access, rapidmind onboarding"
          image="/RapidMind 2025.png"
          type="website"
        />
        <div className="register-form-container">
          <img id='registerImg' src={registerImg} alt="RapidMind Logo" />
          {/* Congratulatory Message */}
          <div className="congratulation-message">
            <div className="congratulation-header">
              <h2>🎉 Félicitations ! 🎉</h2>
            </div>

            <div className="congratulation-content">
              <p className="greeting">
                {userData.gender === 'male' ? 'Monsieur' : userData.gender === 'female' ? 'Madame' : ''} {userData.firstName},
              </p>

              <p className="message-text">
                Merci d'avoir rejoint la communauté <strong>RapidMind</strong> ! 🚀<br />
                Nous sommes ravis de vous compter parmi nos membres et sommes impatients de vous accompagner dans votre parcours.
              </p>

              <p className="message-text">
                Nous avons hâte de vous partager de nouveaux outils et ressources pour vous aider à atteindre vos objectifs. 🧠💡
              </p>

              <p className="welcome-text">
                Bienvenue à bord ! ✨
              </p>

              <p className="signature">
                L'équipe RapidMind
              </p>
            </div>
          </div>

          <div className="user-id-section">
            <h3>Votre ID utilisateur</h3>
            <div className="code-display">
              <span className="code">
                {userData?.id || 'ID non disponible'}
              </span>
              <button
                className="copy-button"
                onClick={() => copyToClipboard(userData?.id || '')}
                title="Copier l'ID"
                disabled={!userData?.id}
              >
                {copied ? '✓ Copié' : '📋 Copier'}
              </button>
            </div>
            <p className="warning">
              🔒 <strong>Votre identifiant unique :</strong> Conservez cet ID en sécurité.
            </p>
          </div>

          <div className="private-code-section">
            <h3>Votre code privé {' '}
              <span
                role="button"
                aria-label="Toggle private code visibility"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowPrivateCode(!showPrivateCode)}
                title={showPrivateCode ? 'Masquer le code' : 'Afficher le code'}
              >
                {showPrivateCode ? '🙈' : '👁️'}
              </span>
            </h3>
            <div className="code-display">
              <span className="code">
                {showPrivateCode
                  ? (userData?.privateCode || 'Code non disponible')
                  : '••••••'
                }
              </span>
              <button
                className="copy-button"
                onClick={() => copyToClipboard(userData?.privateCode || '')}
                title="Copier le code"
                disabled={!userData?.privateCode}
              >
                {copied ? '✓ Copié' : '📋 Copier'}
              </button>
            </div>
            <p className="warning">
              ⚠️ <strong>Important :</strong> Gardez ce code en sécurité et ne le partagez avec personne.
              Il vous sera demandé pour certaines fonctionnalités de l'application.
            </p>
          </div>

          <button
            onClick={handleContinue}
            className="continue-button"
            style={{
              backgroundColor: hoveredButton === 'profile' ? 'yellow' : 'green',
              borderRadius: '15px',
              color: hoveredButton === 'profile' ? 'black' : 'white',
              padding: '10px 20px',
              border: 'none',
              cursor: 'pointer',
              marginRight: '10px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={() => setHoveredButton('profile')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            Continuer vers mon profil
          </button>
          <button
            onClick={() => navigate('/')}
            className="continue-home-button"
            style={{
              backgroundColor: hoveredButton === 'home' ? 'green' : 'yellow',
              borderRadius: '15px',
              color: hoveredButton === 'home' ? 'white' : 'black',
              padding: '10px 20px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={() => setHoveredButton('home')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            Continuer vers Home
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Sign Up for RapidMind AI - Create Your Free Account"
        description="Join RapidMind AI today and start creating with advanced AI tools. Free registration, no credit card required. Access AI-powered content creation platform."
        keywords="sign up rapidmind ai, create ai account, free ai registration, ai platform signup, rapidmind user registration, ai tools account"
        image="/RapidMind 2025.png"
        type="website"
      />
      <div className="register-form-container">
        <img id='registerImg' src={registerImg} alt="RapidMind Logo" />
        <h1>Create Your RapidMind AI Account</h1>
        <form onSubmit={handleSubmit}>
          <input type="text" name="firstName" placeholder="Prénom" onChange={handleChange} />
          {errors.firstName && <span className="error">{errors.firstName}</span>}

          <input type="text" name="lastName" placeholder="Nom" onChange={handleChange} />
          {errors.lastName && <span className="error">{errors.lastName}</span>}

          <input type="email" name="email" placeholder="Email" onChange={handleChange} />
          {errors.email && <span className="error">{errors.email}</span>}

          <select name="gender" value={formData.gender} onChange={handleGenderChange} className="gender-select">
            <option value="">Sélectionnez votre genre</option>
            <option value="male">Monsieur</option>
            <option value="female">Madame</option>
          </select>
          {errors.gender && <span className="error">{errors.gender}</span>}

          <input type="password" name="password" placeholder="Mot de passe" onChange={handleChange} />
          {errors.password && <span className="error">{errors.password}</span>}

          <input type="password" name="confirmPassword" placeholder="Confirmer mot de passe" onChange={handleChange} />
          {errors.confirmPassword && <span className="error">{errors.confirmPassword}</span>}

          <div className="terms-checkbox">
            <label>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={handleCheckboxChange}
              />
              {' '}J'accepte les <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">termes et conditions</a>
            </label>
            {errors.agreedToTerms && <span className="error">{errors.agreedToTerms}</span>}
          </div>
          <br></br>
          <button type="submit" disabled={loading}>
            {loading ? 'Chargement...' : 'S’inscrire'}
          </button>
          <SocialRegisterButtons />
        </form>
      </div>
    </>
  );
};

export default Register;
